import { Link as MuiLink, type LinkProps as MuiLinkProps, type SxProps, type Theme } from '@mui/material';
import { useForkRef } from '@mui/material/utils';
import { type DOMAttributes, forwardRef } from 'react';

import type { WithoutStyleOverrides } from '../../../types/WithoutStyleOverrides';

const BUTTON_ELEMENT = 'button';
const BUTTON_TYPE = 'button';
const BUTTON_ALIGNMENT: SxProps<Theme> = { verticalAlign: 'baseline', textAlign: 'inherit' };
const TEXT_COLOR_PREFIX = 'text';
const MUI_TEXT_COLORS: ReadonlySet<string> = new Set(['textPrimary', 'textSecondary', 'textDisabled']);

type NamedValue<Value> = Value extends string ? (string extends Value ? never : Value) : never;

type LinkColor = NamedValue<MuiLinkProps['color']>;

type ElementEvents = DOMAttributes<HTMLElement>;

type Props = WithoutStyleOverrides<
  Omit<MuiLinkProps, 'ref' | 'color' | 'type' | 'TypographyClasses' | keyof ElementEvents>
> & ElementEvents & {
  color?: LinkColor;
};

function toMuiColor(color: LinkColor | undefined): MuiLinkProps['color'] {
  if (color === undefined || MUI_TEXT_COLORS.has(color) || !color.startsWith(TEXT_COLOR_PREFIX)) {
    return color;
  }

  const textKey = color.slice(TEXT_COLOR_PREFIX.length);

  return `${TEXT_COLOR_PREFIX}.${textKey.charAt(0).toLowerCase()}${textKey.slice(1)}`;
}

// forwardRef, not a `ref` prop: the app still runs React 18, where function components never receive `ref`.
export const Link = forwardRef<HTMLElement, Props>(function Link(props, ref) {
  const elementRef = useForkRef(ref);
  const { href, color, ...linkProps } = props;
  const muiColor = toMuiColor(color);

  if (href === undefined) {
    return (
      <MuiLink
        {...linkProps}
        color={muiColor}
        component={BUTTON_ELEMENT}
        type={BUTTON_TYPE}
        sx={BUTTON_ALIGNMENT}
        ref={elementRef}
      />
    );
  }

  return <MuiLink {...linkProps} color={muiColor} href={href} ref={elementRef} />;
});

export type { Props as LinkProps };
